import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { BackButton } from "./ui/BackButton";

interface IntroductionPageProps {
  onNext: () => void;
  onBackToCover: () => void;
}

export const IntroductionPage = ({ onNext, onBackToCover }: IntroductionPageProps) => {
  return (
    <div className="min-h-screen bg-paper">
      {/* Header */}
      <header className="border-b border-border/50">
        <div className="max-w-3xl mx-auto px-6 py-4 flex justify-between items-center">
          <BackButton onClick={onBackToCover} label="Cover" srLabel="Return to cover page" />
          <span className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
            Introduction
          </span>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-2xl mx-auto px-6 py-16">
        <motion.article
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="space-y-12"
        >
          {/* Title section */}
          <header className="text-center space-y-6">
            <p className="text-xs uppercase tracking-[0.3em] text-muted-foreground">
              Welcome
            </p>
            <h1 className="font-display text-4xl md:text-5xl text-ink tracking-tight">
              Your Spiritual Journey
            </h1>
            <div className="divider-elegant" />
          </header>

          {/* Greeting */}
          <section className="space-y-6 text-lg leading-relaxed text-foreground/90">
            <p className="text-center italic text-muted-foreground">
              Assalamu Alaikum wa Rahmatullahi wa Barakatuh
            </p>
            
            <p>
              This workbook is your personal companion through the blessed month of Ramadan. 
              Each week focuses on a transformative theme, pairing authentic Islamic duas with 
              heartfelt, conversational prayers.
            </p>
          </section>

          {/* Quote box */}
          <blockquote className="border-l-2 border-gold/40 pl-6 py-2 italic text-muted-foreground">
            "Dua is the essence of worship"
            <footer className="mt-2 text-sm not-italic">— Prophet Muhammad ﷺ (Tirmidhi)</footer>
          </blockquote>

          {/* How to use section */}
          <section className="bg-cream rounded-sm p-8 space-y-6">
            <h2 className="font-display text-xl text-ink">How to Use This Workbook</h2>
            <ul className="space-y-4 text-foreground/80">
              <li className="flex items-start gap-4">
                <span className="text-gold text-sm mt-1">01</span>
                <span>Spend 10-15 minutes each morning with the weekly dua</span>
              </li>
              <li className="flex items-start gap-4">
                <span className="text-gold text-sm mt-1">02</span>
                <span>Reflect on the theme throughout your day</span>
              </li>
              <li className="flex items-start gap-4">
                <span className="text-gold text-sm mt-1">03</span>
                <span>Journal your thoughts and experiences</span>
              </li>
              <li className="flex items-start gap-4">
                <span className="text-gold text-sm mt-1">04</span>
                <span>Recite the heartfelt dua in your own moments of need</span>
              </li>
            </ul>
          </section>

          {/* Navigation */}
          <footer className="pt-8 border-t border-border/50 flex justify-end">
            <button
              onClick={onNext}
              className="group flex items-center gap-3 text-sm uppercase tracking-[0.15em] text-espresso hover:text-gold transition-colors"
            >
              Begin Week 1
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </button>
          </footer>
        </motion.article>
      </main>
    </div>
  );
};
